import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Bell, Heart, Moon, Sun, LogIn, LocateFixed, Sparkles, Clock, Loader2, Menu, X, ChevronDown, ShieldCheck, Globe, MoreHorizontal } from "lucide-react";
import { countries } from "../../mock";
import { flagFor } from "../../utils/flags";
import { useTheme } from "../../context/ThemeContext";
import { useFavourites } from "../../context/FavouritesContext";
import { useAuth } from "../../context/AuthContext";
import { useLocationFilter } from "../../context/LocationContext";
import { useLang, TRANSLATION_ENABLED } from "../../context/LanguageContext";
import { AI_ENABLED } from "../../featureFlags";
import { useToast } from "../../hooks/use-toast";
import { TouristButton } from "./TouristMode";
import api from "../../api";

const TopBar = ({ onMenu, mobileNav }) => {
  const { theme, toggleTheme } = useTheme();
  const { favs } = useFavourites();
  const { user, loading: authLoading } = useAuth();
  const { setCountry, setCity, city, setQuery, setGps, setPlace, gps, country, place } = useLocationFilter();
  const { t, lang, setLang, languages } = useLang();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [aiMode, setAiMode] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [suggOpen, setSuggOpen] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lp-recent-searches") || "[]"); } catch { return []; }
  });
  const searchRef = useRef(null);
  const [loc, setLoc] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const locRef = useRef(null);
  const langRef = useRef(null);
  const moreRef = useRef(null);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lp-read-notifs") || "[]"); } catch { return []; }
  });
  const notifRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;          // don't fill the bell with guest deals before we know who this is
    if (user) {
      api.get("/my/notifications").then((r) => {
        setNotifs((r.data || []).map((n) => ({ id: n.id, dealId: n.dealId, link: n.link, title: n.title, body: n.body, nearExpiry: false, read: n.read })));
      }).catch(() => setNotifs([]));
      return;
    }
    api.get("/deals").then((r) => {
      const items = [...r.data]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 6)
        .map((d) => ({
          id: d.id,
          dealId: d.id,
          title: d.nearExpiry ? `Last chance: ${d.title}` : `New deal: ${d.title}`,
          body: `${d.discount}% OFF at ${d.business} · ${d.location}`,
          nearExpiry: d.nearExpiry,
        }));
      setNotifs(items);
    }).catch(() => {});
  }, [user, authLoading]);

  useEffect(() => {
    const onClick = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => { api.get("/deal-locations").then((r) => setLocations(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const onClick = (e) => { if (locRef.current && !locRef.current.contains(e.target)) setLocOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // The right-rail "Change" link asks us to open this picker.
  useEffect(() => {
    const open = () => {
      setLocOpen(true);
      locRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    window.addEventListener("lp-open-location", open);
    return () => window.removeEventListener("lp-open-location", open);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSuggOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (aiMode || q.trim().length < 2) { setSugg(null); return; }
    const t = setTimeout(() => {
      api.get("/search/suggest", { params: { q: q.trim() } })
        .then((r) => { setSugg(r.data); setSuggOpen(true); })
        .catch(() => setSugg(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q, aiMode]);

  const saveRecent = (term) => {
    const t = term.trim();
    if (!t) return;
    const next = [t, ...recent.filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(0, 5);
    setRecent(next);
    localStorage.setItem("lp-recent-searches", JSON.stringify(next));
  };

  const removeRecent = (term) => {
    const next = recent.filter((r) => r !== term);
    setRecent(next);
    localStorage.setItem("lp-recent-searches", JSON.stringify(next));
    if (!next.length) setSuggOpen(false);
  };

  const unread = notifs.filter((n) => (n.read != null ? !n.read : !readIds.includes(n.id))).length;

  const toggleNotif = () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next && notifs.length) {
      if (user) {
        api.post("/my/notifications/read").catch(() => {});
        setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
      } else {
        const ids = notifs.map((n) => n.id);
        setReadIds(ids);
        localStorage.setItem("lp-read-notifs", JSON.stringify(ids));
      }
    }
  };

  const aiExamples = ["cheap dinner near me", "kids activities this weekend", "50% off groceries", "half-price coffee today"];

  const openNotif = (n) => {
    setNotifOpen(false);
    if (n.link) navigate(n.link);
    else if (n.dealId) navigate(`/deal/${n.dealId}`);
    else navigate("/browse");
  };

  const deleteNotif = (e, id) => {
    e.stopPropagation();
    setNotifs((prev) => prev.filter((n) => n.id !== id));
    if (user) api.delete(`/my/notifications/${id}`).catch(() => {});
    else {
      const next = [...new Set([...readIds, id])];
      setReadIds(next);
      localStorage.setItem("lp-read-notifs", JSON.stringify(next));
    }
  };

  const clearAllNotifs = () => {
    const ids = notifs.map((n) => n.id);
    setNotifs([]);
    setNotifOpen(false);
    if (user) api.delete("/my/notifications").catch(() => {});
    else {
      const next = [...new Set([...readIds, ...ids])];
      setReadIds(next);
      localStorage.setItem("lp-read-notifs", JSON.stringify(next));
    }
  };

  const runAiPrompt = (prompt) => {
    setQ(prompt);
    navigate("/browse", { state: { aiQuery: prompt, gps } });
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSuggOpen(false);
    if (aiMode) {
      if (!q.trim()) return;
      saveRecent(q);
      navigate("/browse", { state: { aiQuery: q, gps } });
      return;
    }
    saveRecent(q);
    setQuery(q);
    navigate("/browse");
  };

  const submitLoc = async (e) => {
    if (e) e.preventDefault();
    if (!loc.trim()) { resetToAnywhere(); return; }
    setGeoLoading(true);
    try {
      const r = await api.get("/geocode", { params: { q: loc } });
      setGps({ lat: r.data.lat, lng: r.data.lng });
      setPlace(r.data.formatted || loc);
      const match = countries.find((c) => c.name === r.data.country);
      if (match) setCountry(match.name);
      toast({ title: t("tb.locationSet"), description: `Showing deals near ${r.data.formatted || loc}.` });
      navigate("/browse");
    } catch {
      toast({ title: t("tb.placeNotFound"), description: t("tb.tryADifferentCity") });
    } finally { setGeoLoading(false); }
  };

  const resetToAnywhere = () => {
    setGps(null);
    setPlace("");
    setCountry("");
    setLoc("");
    toast({ title: t("tb.showingDealsEverywhere"), description: t("tb.browsingDealsFromAll") });
    navigate("/browse");
  };

  const pickCountry = (val) => { setGps(null); setPlace(""); setCountry(val); navigate("/browse"); if (!val) setLocOpen(false); };
  const pickCity = (val) => { setCity(val); navigate("/browse"); setLocOpen(false); };
  const citiesForCountry = (locations.find((l) => l.country === country)?.cities) || [];
  const locLabel = place ? place.split(",")[0] : (city || country || t("topbar.anywhere"));
  const currentLang = languages.find((l) => l.code === lang) || languages[0];

  const nearMe = async () => {
    if (!navigator.geolocation) { toast({ title: t("tb.notSupported"), description: t("tb.geolocationIsnTAvailable") }); return; }
    setLocating(true);
    const getPos = (opts) => new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, opts));
    try {
      let pos;
      try {
        pos = await getPos({ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
      } catch (e1) {
        if (e1 && e1.code === 1) throw e1; // permission denied — don't retry
        pos = await getPos({ enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }); // fallback (wifi/network)
      }
      const g = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setGps(g);
      setCountry("");
      setLocating(false);
      let label = "Near me";
      try {
        const { data } = await api.get("/reverse-geocode", { params: g });
        label = data.city || (data.formatted ? data.formatted.split(",")[0] : "") || "Near me";
      } catch { /* keep default */ }
      setPlace(label);
      toast({ title: t("tb.locationFound"), description: `Showing deals closest to ${label}.` });
      navigate("/browse");
    } catch (err) {
      setLocating(false);
      if (err && err.code === 1) {
        toast({ title: t("tb.locationPermissionDenied"), description: t("tb.allowLocationForThis") });
      } else if (err && err.code === 3) {
        toast({ title: t("tb.locationTimedOut"), description: t("tb.couldnTGetA") });
      } else {
        toast({ title: t("tb.locationUnavailable"), description: t("tb.weCouldnTDetermine") });
      }
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      {onMenu && (
        <button onClick={onMenu} className="lg:hidden order-1 shrink-0 h-11 w-11 rounded-xl bg-card border border-border flex items-center justify-center text-foreground" aria-label="Menu" data-testid="sidebar-toggle">
          {mobileNav ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}
      <div ref={searchRef} className="relative order-3 w-full lg:order-1 lg:w-auto lg:flex-1 min-w-0">
      <form onSubmit={submitSearch} className="flex items-stretch h-11 bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <button type="submit" title={t("tb.searchDeals")} data-testid="topbar-search-btn" className="flex items-center pl-4 pr-2 text-muted-foreground hover:text-[#16a34a] transition-colors shrink-0">
          <Search size={18} />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onFocus={() => { if ((sugg && q.trim().length >= 2) || (!q.trim() && recent.length)) setSuggOpen(true); }}
            onKeyDown={(e) => { if (e.key === "Escape") setSuggOpen(false); }}
            placeholder={aiMode ? t("topbar.aiPlaceholder") : t("topbar.searchPlaceholder")} className="w-full min-w-0 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground" data-testid="topbar-search-input" />
        </div>
        {AI_ENABLED && (<button type="button" onClick={() => { setAiMode((v) => !v); setSuggOpen(false); }} title={t("tb.toggleAiAssistedSearch")} data-testid="ai-search-toggle" className={`self-center flex items-center gap-1.5 mr-1.5 h-8 px-2.5 rounded-lg text-xs font-bold transition-colors shrink-0 ${aiMode ? "bg-[#16a34a] text-white shadow" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
          <Sparkles size={14} /> <span className="hidden sm:inline">{t("topbar.askAi")}</span>
        </button>)}
        <button type="button" onClick={nearMe} title={t("tb.findDealsNearMe")} className="flex items-center gap-1.5 px-4 border-l border-border text-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] transition-colors font-semibold text-sm shrink-0" data-testid="near-me-btn">
          <LocateFixed size={18} className={locating ? "animate-pulse" : ""} />
          <span className="hidden xl:inline">{t("topbar.nearMe")}</span>
        </button>
      </form>
      {suggOpen && !aiMode && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden py-1.5" data-testid="search-suggestions">
          {!q.trim() && recent.length > 0 && (
            <>
              <p className="px-3.5 pt-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{t("tb.recentSearches")}</p>
              {recent.map((r, i) => (
                <div key={r} className="flex items-center hover:bg-secondary transition-colors group">
                  <button type="button" data-testid={`recent-search-${i}`}
                    onClick={() => { setQ(r); setQuery(r); setSuggOpen(false); navigate("/browse"); }}
                    className="flex-1 min-w-0 flex items-center gap-2.5 px-3.5 py-2 text-left text-sm text-foreground">
                    <Clock size={14} className="text-muted-foreground shrink-0" /> <span className="truncate">{r}</span>
                  </button>
                  <button type="button" data-testid={`recent-search-remove-${i}`} title={t("tb.removeFromRecentSearches")} aria-label={`Remove "${r}" from recent searches`}
                    onClick={(e) => { e.stopPropagation(); removeRecent(r); }}
                    className="shrink-0 px-3 py-2 text-muted-foreground hover:text-rose-500 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </>
          )}
          {q.trim().length >= 2 && sugg && (
            <>
              {sugg.didYouMean && (
                <button type="button" data-testid="suggest-did-you-mean"
                  onClick={() => { setQ(sugg.didYouMean); setQuery(sugg.didYouMean); saveRecent(sugg.didYouMean); setSuggOpen(false); navigate("/browse"); }}
                  className="w-full px-3.5 py-2 text-left text-sm text-foreground hover:bg-secondary transition-colors">
                  {t("tb.didYouMean")} <span className="font-bold text-[#16a34a]">{sugg.didYouMean}</span>?
                </button>
              )}
              {(sugg.deals || []).map((d) => (
                <button key={d.id} type="button" data-testid={`suggest-deal-${d.id}`}
                  onClick={() => { setSuggOpen(false); navigate(`/deal/${d.id}`); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left hover:bg-secondary transition-colors">
                  <span className="h-7 w-7 rounded-lg bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center shrink-0 text-[10px] font-bold text-[#16a34a]">{d.discount}%</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground truncate">{d.title}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">{d.business} · {d.category}</span>
                  </span>
                </button>
              ))}
              {(sugg.businesses || []).map((b, i) => (
                <button key={b} type="button" data-testid={`suggest-business-${i}`}
                  onClick={() => { setSuggOpen(false); navigate(`/business/${encodeURIComponent(b)}`); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-sm text-foreground hover:bg-secondary transition-colors">
                  <span className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center shrink-0 text-xs font-bold text-muted-foreground">{b[0]}</span>
                  <span className="truncate">{b}</span> <span className="ml-auto text-[11px] text-muted-foreground shrink-0">Business</span>
                </button>
              ))}
              {(sugg.categories || []).map((c, i) => (
                <button key={c} type="button" data-testid={`suggest-category-${i}`}
                  onClick={() => { setSuggOpen(false); navigate("/browse", { state: { category: c } }); }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-sm text-foreground hover:bg-secondary transition-colors">
                  <Search size={14} className="text-muted-foreground shrink-0 ml-1.5" />
                  <span className="truncate">{c}</span> <span className="ml-auto text-[11px] text-muted-foreground shrink-0">Category</span>
                </button>
              ))}
              {(sugg.deals || []).length === 0 && (sugg.businesses || []).length === 0 && (sugg.categories || []).length === 0 && (
                <p className="px-3.5 py-2 text-sm text-muted-foreground" data-testid="suggest-empty">{t("tb.noQuickMatchesPress")}</p>
              )}
              {AI_ENABLED && (<button type="button" data-testid="suggest-ask-ai"
                onClick={() => { setSuggOpen(false); saveRecent(q); navigate("/browse", { state: { aiQuery: q, gps } }); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] transition-colors border-t border-border mt-1">
                <Sparkles size={14} className="shrink-0" /> <span className="truncate">Ask AI: “{q.trim()}”</span>
              </button>)}
            </>
          )}
        </div>
      )}
      </div>

      <div ref={locRef} className="relative order-4 w-full lg:order-1 lg:w-auto flex items-center lg:shrink-0" data-testid="location-picker">
        <div className="inline-flex items-center h-11 bg-card border border-border rounded-xl shadow-sm">
        <button type="button" onClick={() => setLocOpen((o) => !o)} data-testid="location-picker-btn" className="flex items-center gap-1.5 pl-3 pr-1 h-full text-sm text-foreground max-w-[220px]">
          <MapPin size={16} className="text-[#16a34a] shrink-0" />
          <span className="truncate">{locLabel}</span>
          <ChevronDown size={14} className={`text-muted-foreground shrink-0 transition-transform ${locOpen ? "rotate-180" : ""}`} />
        </button>
        {(gps || place || country) && (
          <button type="button" onClick={(e) => { e.stopPropagation(); resetToAnywhere(); }} title={t("tb.showDealsEverywhere")} data-testid="location-clear-inline" className="pr-2.5 text-muted-foreground hover:text-rose-500 shrink-0">
            <X size={14} />
          </button>
        )}
        </div>
        {locOpen && (
          <div className="absolute left-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-xl z-50 p-3 space-y-3" data-testid="location-picker-menu">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t("topbar.country")}</label>
              <select value={country} onChange={(e) => pickCountry(e.target.value)} data-testid="country-select" className="mt-1 w-full h-9 rounded-lg border border-border bg-card px-2 text-sm text-foreground outline-none focus:border-[#16a34a]">
                <option value="">🌍 {t("topbar.anywhere")}</option>
                {locations.map((l) => <option key={l.country} value={l.country}>{`${flagFor(l.country)} ${l.country}`}</option>)}
              </select>
            </div>
            {country && citiesForCountry.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{t("topbar.city")}</label>
                <select value={city} onChange={(e) => pickCity(e.target.value)} data-testid="city-select" className="mt-1 w-full h-9 rounded-lg border border-border bg-card px-2 text-sm text-foreground outline-none focus:border-[#16a34a]">
                  <option value="">{t("topbar.allCities")}</option>
                  {citiesForCountry.map((ci) => <option key={ci} value={ci}>{ci}</option>)}
                </select>
              </div>
            )}
            <button type="button" onClick={() => { nearMe(); setLocOpen(false); }} data-testid="picker-near-me" className="w-full flex items-center justify-center gap-2 h-9 rounded-lg bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a] font-semibold text-sm hover:opacity-90 transition-opacity">
              <LocateFixed size={15} className={locating ? "animate-pulse" : ""} /> {t("topbar.useMyLocation")}
            </button>
            <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground" data-testid="location-privacy-note">
              <ShieldCheck size={13} className="text-[#16a34a] shrink-0 mt-0.5" />
              {t("topbar.privacyNote")}
            </p>
            <div className="flex items-center gap-2 border-t border-border pt-2.5">
              <MapPin size={15} className="text-muted-foreground shrink-0" />
              <input value={loc} onChange={(e) => setLoc(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitLoc(); setLocOpen(false); } }} placeholder={t("topbar.typePlace")} className="flex-1 min-w-0 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground" data-testid="location-search-input" />
              <button type="button" onClick={() => { submitLoc(); setLocOpen(false); }} title={t("tb.searchThisPlace")} className="text-[#16a34a] hover:text-[#15803d] shrink-0" data-testid="location-search-go">
                {geoLoading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              </button>
            </div>
            {(gps || place || country) && (
              <button type="button" onClick={() => { resetToAnywhere(); setLocOpen(false); }} data-testid="location-clear-btn" className="w-full flex items-center justify-center gap-1 text-xs text-rose-500 font-semibold hover:underline">
                <X size={13} /> {t("topbar.clearEverywhere")}
              </button>
            )}
          </div>
        )}
      </div>

      {AI_ENABLED && aiMode && (
        <div className="order-4 w-full flex flex-wrap items-center gap-2 pt-0.5" data-testid="ai-example-prompts">
          <span className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1"><Sparkles size={12} className="text-[#16a34a]" /> {t("topbar.try")}</span>
          {aiExamples.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => runAiPrompt(ex)}
              data-testid={`ai-prompt-${ex.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#e7f6ec] text-[#0e6b3e] hover:bg-[#16a34a] hover:text-white transition-colors dark:bg-[#16341f] dark:text-[#7ee0a3]"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      <div className="order-2 ml-auto lg:ml-0 flex items-center gap-1.5 sm:gap-2 lg:gap-3 shrink-0">
      <div className="hidden lg:block"><TouristButton variant="header" /></div>

      <div className="relative shrink-0 hidden lg:block" ref={langRef}>
        {TRANSLATION_ENABLED && (<>
        <button onClick={() => setLangOpen((o) => !o)} data-testid="language-toggle" title={t("lang.label")} aria-label={t("lang.label")}
          className="h-11 px-2.5 rounded-xl bg-card border border-border flex items-center gap-1.5 text-foreground hover:bg-secondary transition-colors">
          <Globe size={18} />
          <span className="text-xs font-bold">{currentLang.short}</span>
          <ChevronDown size={13} className={`text-muted-foreground transition-transform ${langOpen ? "rotate-180" : ""}`} />
        </button>
        {langOpen && (
          <div className="absolute right-0 mt-2 w-44 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden p-1" data-testid="language-menu">
            {languages.map((l) => (
              <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} data-testid={`language-option-${l.code}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${lang === l.code ? "bg-[#e7f6ec] text-[#16a34a] dark:bg-[#16341f]" : "text-foreground hover:bg-secondary"}`}>
                <span>{l.label}</span>
                <span className="text-[11px] font-bold text-muted-foreground">{l.short}</span>
              </button>
            ))}
          </div>
        )}
        </>)}
      </div>

      <button onClick={toggleTheme} className="hidden lg:flex shrink-0 h-11 w-11 rounded-xl bg-card border border-border items-center justify-center text-foreground hover:bg-secondary transition-colors" aria-label={t("tb.toggleTheme")} data-testid="theme-toggle">
        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div className="relative shrink-0" ref={notifRef}>
        <button onClick={toggleNotif} className="relative h-11 w-11 rounded-xl bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Notifications">
          <Bell size={20} />
          {unread > 0 && (<span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-[#16a34a] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>)}
        </button>
        {notifOpen && (
          <div className="fixed inset-x-3 top-32 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-80 bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden" data-testid="notifications-dropdown">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="font-bold text-foreground">{t("topbar.notifications")}</span>
              <div className="flex items-center gap-3">
                {notifs.length > 0 && (
                  <button onClick={clearAllNotifs} data-testid="notif-clear-all"
                    className="text-xs font-semibold text-muted-foreground hover:text-rose-500 transition-colors">
                    {t("topbar.clearAll")}
                  </button>
                )}
                <span className="text-xs text-muted-foreground">{t("topbar.recent", { n: notifs.length })}</span>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto no-scrollbar">
              {notifs.length === 0 && <p className="px-4 py-6 text-sm text-muted-foreground text-center">{t("topbar.noNotifs")}</p>}
              {notifs.map((n) => (
                <div key={n.id} className="flex items-stretch hover:bg-secondary transition-colors group border-b border-border last:border-0" data-testid="notif-item">
                  <button onClick={() => openNotif(n)} className="flex-1 min-w-0 text-left px-4 py-3 flex gap-3">
                    <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${n.nearExpiry ? "bg-[#fde8ee]" : "bg-[#e7f6ec]"}`}>
                      {n.nearExpiry ? <Clock size={16} className="text-rose-500" /> : <Sparkles size={16} className="text-[#16a34a]" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground truncate">{n.title}</span>
                      <span className="block text-xs text-muted-foreground truncate">{n.body}</span>
                    </span>
                  </button>
                  <button onClick={(e) => deleteNotif(e, n.id)} title={t("topbar.dismiss")} aria-label={t("topbar.dismiss")}
                    data-testid="notif-delete" className="shrink-0 px-3 text-muted-foreground hover:text-rose-500 transition-colors">
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
            <Link to="/browse" onClick={() => setNotifOpen(false)} className="block text-center py-3 text-sm font-semibold text-[#16a34a] hover:bg-secondary transition-colors border-t border-border">{t("topbar.viewAllDeals")}</Link>
          </div>
        )}
      </div>

      <Link to="/favourites" className="relative shrink-0 h-11 w-11 rounded-xl bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Favourites">
        <Heart size={20} />
        {favs.length > 0 && (<span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{favs.length}</span>)}
      </Link>

      <div className="relative shrink-0 lg:hidden" ref={moreRef}>
        <button onClick={() => setMoreOpen((o) => !o)} data-testid="topbar-more-btn" aria-label={t("topbar.more")}
          className="h-11 w-11 rounded-xl bg-card border border-border flex items-center justify-center text-foreground hover:bg-secondary transition-colors">
          <MoreHorizontal size={20} />
        </button>
        {moreOpen && (
          <div className="absolute right-0 mt-2 w-[17rem] bg-card border border-border rounded-2xl shadow-xl z-50 p-3 space-y-3" data-testid="topbar-more-menu">
            <button onClick={toggleTheme} data-testid="theme-toggle-mobile"
              className="w-full h-11 px-3 rounded-xl bg-secondary flex items-center justify-between text-sm font-semibold text-foreground">
              {theme === "dark" ? t("topbar.lightMode") : t("topbar.darkMode")}
              {theme === "dark" ? <Sun size={17} className="text-[#f5b942]" /> : <Moon size={17} />}
            </button>
            {TRANSLATION_ENABLED && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">{t("lang.label")}</p>
                <div className="grid grid-cols-5 gap-1.5">
                  {languages.map((l) => (
                    <button key={l.code} onClick={() => { setLang(l.code); setMoreOpen(false); }} data-testid={`language-option-${l.code}`}
                      className={`h-10 rounded-xl text-xs font-bold transition-colors ${lang === l.code ? "bg-[#16a34a] text-white" : "bg-secondary text-foreground hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
                      {l.short}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div onClick={() => setMoreOpen(false)}><TouristButton variant="sheet" /></div>
          </div>
        )}
      </div>

      {user ? (
        <Link to="/account" className="shrink-0 flex items-center gap-2.5 h-11 pl-1 pr-3 rounded-xl hover:bg-secondary transition-colors">
          <span className="h-9 w-9 rounded-full bg-[#16a34a] text-white font-bold flex items-center justify-center ring-2 ring-[#f5b942]">{user.name?.[0] || "L"}</span>
          <span className="hidden md:inline text-sm font-semibold text-foreground">{t("topbar.hello", { name: user.name.split(" ")[0] })}</span>
        </Link>
      ) : (
        <Link to="/login" className="shrink-0 flex items-center gap-2 h-11 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-4 rounded-xl transition-colors">
          <LogIn size={16} /> <span className="hidden sm:inline">{t("topbar.login")}</span>
        </Link>
      )}
      </div>
    </div>
  );
};

export default TopBar;
